import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MagnifyingGlass, MapPin, Check, Lock } from 'phosphor-react';
import { motion } from 'framer-motion';
import SBack from '@/components/ui/molecules/SBack';
import SLoader from '@/components/ui/atoms/SLoader';
import { ThemeToggle } from '@/components/ui/atoms/ThemeToggle';
import { useLocations } from '@/hooks/useLocations';
import { useUserLocation } from '@/hooks/useUserLocation';
import { useMembership } from '@/hooks/useMembership';
import { useMember } from '@/hooks/useMember';
import { LocationData } from '@/types/location.types';
import './LocationSwap.css';

function formatAddress(address: any): string {
  if (!address) return '';
  if (typeof address === 'string') return address;
  return [address.line1, address.city, address.state].filter(Boolean).join(', ');
}

const LocationSwap: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Walk up the DOM to find the nearest scrollable ancestor
  const getScrollParent = (el: HTMLElement): HTMLElement => {
    let node: HTMLElement | null = el.parentElement;
    while (node) {
      const { overflowY } = window.getComputedStyle(node);
      if (overflowY === 'auto' || overflowY === 'scroll') return node;
      node = node.parentElement;
    }
    return document.documentElement;
  };

  const { locations, isLoading, error } = useLocations();
  const { location: userLocation } = useUserLocation();
  const { data: membership } = useMembership();
  const { memberProfile } = useMember();

  // paidGuestAccessAllowed defaults to true if the plan hasn't loaded yet or the flag is unset
  const paidGuestAccessAllowed = membership?.paidGuestAccessAllowed ?? true;
  // dropInBookingBlocked defaults to false so an unresolved profile never flashes a restriction banner
  const dropInBookingBlocked = memberProfile?.dropInBookingBlocked ?? false;

  useEffect(() => {
    const fromState = location.state?.selectedLocation;
    if (fromState) setSelectedLocationId(fromState.id);
    else if (userLocation) setSelectedLocationId(userLocation.id);
  }, [location.state, userLocation]);

  // After locations render, scroll the selected row into the centre of the viewport
  useEffect(() => {
    if (!locations.length || !selectedLocationId) return;
    const timer = setTimeout(() => {
      const el = bodyRef.current?.querySelector<HTMLElement>(`[data-loc-id="${selectedLocationId}"]`);
      if (!el) return;
      const scrollParent = getScrollParent(el);
      const parentRect = scrollParent.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const offset = scrollParent.scrollTop + elRect.top - parentRect.top
        - scrollParent.clientHeight / 2 + el.clientHeight / 2;
      scrollParent.scrollTo({ top: offset, behavior: 'smooth' });
    }, 300);
    return () => clearTimeout(timer);
  }, [locations.length, selectedLocationId]);

  const filteredLocations = useMemo(() => {
    if (!searchQuery.trim()) return locations;
    const q = searchQuery.toLowerCase();
    return locations.filter(loc => {
      const nameMatch = loc.name.toLowerCase().includes(q);
      const addrMatch = formatAddress(loc.address).toLowerCase().includes(q);
      return nameMatch || addrMatch;
    });
  }, [locations, searchQuery]);

  const isGuestLocation = (loc: LocationData) => loc.id !== userLocation?.id;

  const handleSelect = (loc: LocationData) => {
    if (!paidGuestAccessAllowed && isGuestLocation(loc)) return;
    setSelectedLocationId(loc.id);
    const formattedLocation = {
      id: loc.id,
      name: loc.name,
      address: formatAddress(loc.address),
      timezone: loc.timezone,
    };
    navigate('/booking-type', { state: { selectedLocation: formattedLocation } });
  };

  return (
    <div className="ls">
      {/* Header */}
      <div className="ls__header">
        <SBack onClick={() => navigate(-1)} />
        <h1 className="ls__title">Choose Location</h1>
        <ThemeToggle />
      </div>

      <div className="ls__body" ref={bodyRef}>
        {/* Search */}
        <div className="ls__search">
          <MagnifyingGlass className="ls__search-icon" size={16} color="#9e9a90" />
          <input
            className="ls__search-input"
            placeholder="Search locations..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Default location — always shows the member's home location */}
        {userLocation && (
          <>
            <span className="ls__label">Your Location</span>
            <div className="ls__list">
              <div className="ls__card ls__card--current">
                <div className="ls__pin">
                  <MapPin size={18} color="rgba(255,255,255,0.6)" weight="fill" />
                </div>
                <div className="ls__card-info">
                  <span className="ls__card-name">{userLocation.name}</span>
                  <span className="ls__card-addr">{formatAddress(userLocation.address)}</span>
                </div>
              </div>
            </div>
          </>
        )}

        {/* All locations */}
        <span className="ls__label">All Locations</span>

        {dropInBookingBlocked ? (
          <div className="ls__guest-notice">
            <Lock size={13} weight="bold" />
            <span>Drop-in booking is currently unavailable for your account.</span>
          </div>
        ) : !paidGuestAccessAllowed ? (
          <div className="ls__guest-notice">
            <Lock size={13} weight="bold" />
            <span>Drop in bookings are not included in your current plan</span>
          </div>
        ) : null}

        {isLoading ? (
          <div className="ls__state"><SLoader /></div>
        ) : error ? (
          <div className="ls__state">Failed to load locations. Please try again.</div>
        ) : filteredLocations.length === 0 ? (
          <div className="ls__state">No locations found</div>
        ) : (
          <div className="ls__list">
            {filteredLocations.map((loc, i) => {
              const isActive = selectedLocationId === loc.id;
              const isLocked = !paidGuestAccessAllowed && isGuestLocation(loc);
              return (
                <motion.button
                  key={loc.id}
                  data-loc-id={loc.id}
                  className={`ls__card ${isActive ? 'ls__card--active' : ''} ${isLocked ? 'ls__card--locked' : ''}`}
                  onClick={() => handleSelect(loc)}
                  disabled={isLocked}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: i * 0.04, ease: 'easeOut' }}
                >
                  <div className="ls__pin">
                    <MapPin size={18} color={isLocked ? '#9e9a90' : isActive ? '#c99535' : '#9e9a90'} weight={isActive && !isLocked ? 'fill' : 'regular'} />
                  </div>
                  <div className="ls__card-info">
                    <span className="ls__card-name">{loc.name}</span>
                    <span className="ls__card-addr">{formatAddress(loc.address)}</span>
                    {isLocked && <span className="ls__card-plan-note">Not available on your plan</span>}
                  </div>
                  {isActive && !isLocked && (
                    <div className="ls__check">
                      <Check size={12} color="#0b0b0a" weight="bold" />
                    </div>
                  )}
                  {isLocked && (
                    <div className="ls__lock">
                      <Lock size={13} color="#9e9a90" weight="bold" />
                    </div>
                  )}
                </motion.button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationSwap;
